/**
 * The herb farm below deck (ADR 0033, lifted from brad-airship
 * `airship-farm.js`): boards with real gaps over a buried subfloor, the walls
 * from the room grid (timber framing, the north wall the ship's side with
 * frames between the portlights and deck beams on hanging knees, planked
 * lining between the frames), seed drawers and suspended shelves on the hull
 * wall, the raised plots with their soil, edging and crop, the potion bench
 * with its three labelled bottles, the drying rack, the watering can, the
 * fresh-water tank on the forward bulkhead and the companionway ladder. Every
 * footprint is the room's (walls and doors from `@ab/scene`'s layout,
 * workstations from the scene record).
 *
 * Changed from the prototype: the kit's materials and finish (cutaway and the
 * radiance volume); tags through `tagMesh`. The farm loop is post-MVP, so the
 * plots show the prototype's six authored states (`plotStates`, by the plot's
 * `index`) and the crop's colour is set here, at build, from the prototype's
 * per-state colours (the prototype's per-frame `updateFixtures` set it from
 * the mock state). `stateGeometry` is kept for when the loop arrives.
 */
import * as THREE from 'three';
import { mergeGeometries } from 'three/addons/utils/BufferGeometryUtils.js';
import type { RoomBuild } from './area.ts';
import { type ShipKit, type Vec3, at, isMesh, tag, tagMesh } from './kit.ts';
import { addCompanionLadder, addPortlight, hullFrameStations, hullGlazing, portlightStations } from './room-kit.ts';
import type { DynamicMesh } from './types.ts';

type PlotState = 'empty' | 'seeded' | 'growing' | 'ready' | 'harvested';
type CropStage = 'seeded' | 'growing' | 'ready';

/** The six plots' authored states (brad-airship `data/interactables.json`, plot-1 to plot-6), by the plot's `index`. */
const plotStates: readonly PlotState[] = ['empty', 'seeded', 'growing', 'ready', 'harvested', 'ready'];

/** The crop's colour per stage (`airship-farm.js` `stateColors`). */
const cropColors: Readonly<Record<CropStage, string>> = { seeded: '#78964f', growing: '#4f7955', ready: '#6b914c' };

const isCrop = (s: PlotState): s is CropStage => s === 'seeded' || s === 'growing' || s === 'ready';

export const buildFarm = (kit: ShipKit, room: RoomBuild, seed = 1): { root: THREE.Group; dynamic: DynamicMesh[] } => {
  const floor = kit.get('floor');
  const trim = kit.get('trim');
  const timber = kit.get('timber');
  const green = kit.get('green');
  const red = kit.get('red');
  const blue = kit.get('blue');
  const warm = kit.get('warm');
  const cream = kit.get('cream');
  const soil = kit.get('soil');
  const marker = kit.get('marker');
  const root = new THREE.Group();
  const dynamic: DynamicMesh[] = [];
  root.name = `farm:${room.id}`;
  // The seed names the build (the prototype drew nothing at random here).
  root.userData['seed'] = seed;
  const boardLight = kit.own(kit.finish(floor.clone()));
  boardLight.color.lerp(new THREE.Color('#ad926e'), 0.14);
  // Daylight comes in through the portlights in the ship's side.
  const glazing = hullGlazing(kit);
  let wallCut = false;
  const mesh = (g: THREE.BufferGeometry, mat: THREE.Material, x = 0, y = 0, z = 0): THREE.Mesh => {
    const o = new THREE.Mesh(tag(g, wallCut), mat);
    o.position.set(x, y, z);
    root.add(o);
    return o;
  };
  const box = (mat: THREE.Material, x: number, y: number, z: number, w: number, h: number, d: number): THREE.Mesh => mesh(new THREE.BoxGeometry(w, h, d), mat, x, y, z);
  const cyl = (mat: THREE.Material, x: number, y: number, z: number, r: number, h: number, rt = r, n = 8): THREE.Mesh => mesh(new THREE.CylinderGeometry(rt, r, h, n), mat, x, y, z);
  const beam = (mat: THREE.Material, a: Vec3, b: Vec3, r = 0.04): THREE.Mesh => {
    const aa = new THREE.Vector3(...a);
    const bb = new THREE.Vector3(...b);
    const v = bb.clone().sub(aa);
    const mid = aa.add(bb).multiplyScalar(0.5);
    const o = mesh(new THREE.CylinderGeometry(r, r, v.length(), 5), mat, mid.x, mid.y, mid.z);
    o.quaternion.setFromUnitVectors(new THREE.Vector3(0, 1, 0), v.normalize());
    return o;
  };
  const ring = (mat: THREE.Material, x: number, y: number, z: number, r: number, t = 0.03, vertical = false): THREE.Mesh => {
    const o = mesh(new THREE.TorusGeometry(r, t, 4, 12), mat, x, y, z);
    if (!vertical) o.rotation.x = Math.PI / 2;
    return o;
  };
  const leaf = (group: THREE.Object3D, x: number, y: number, z: number, s: number, angle: number, mat: THREE.Material): THREE.Mesh => {
    const g = new THREE.SphereGeometry(1, 5, 3);
    g.scale(s, 0.045, s * 0.36);
    const o = new THREE.Mesh(tag(g, wallCut), mat);
    o.position.set(x, y, z);
    o.rotation.y = angle;
    o.rotation.z = 0.3;
    group.add(o);
    return o;
  };
  const h = room.width / 2;
  const d = room.depth / 2;
  // A real gap separates the floor boards from each other and from their buried subfloor.
  for (const s of room.localSurfaces) {
    box(trim, (s.x0 + s.x1) / 2, -0.15, (s.z0 + s.z1) / 2, s.x1 - s.x0, 0.17, s.z1 - s.z0);
    const count = Math.ceil((s.z1 - s.z0) / 0.42);
    const width = (s.z1 - s.z0) / count;
    for (let n = 0; n < count; n += 1) {
      const z = s.z0 + (n + 0.5) * width;
      const split = s.x0 + 3 + ((n * 7) % 13) * 0.38;
      for (const [a, b] of [
        [s.x0, split],
        [split, s.x1],
      ] as const) {
        box((n * 7 + Math.round(a * 11)) % 5 === 0 ? boardLight : floor, (a + b) / 2, -0.032, z, b - a - 0.015, 0.064, width - 0.018);
        for (const xx of [a + 0.12, b - 0.12]) {
          cyl(trim, xx, 0.006, z - 0.09, 0.012, 0.012, 0.012, 4);
          cyl(trim, xx, 0.006, z + 0.09, 0.012, 0.012, 0.012, 4);
        }
        if (n % 3 === 0) box(timber, (a + b) / 2, 0.004, z + 0.1, (b - a) * 0.65, 0.008, 0.013);
      }
    }
  }
  wallCut = true;
  // Segmented wall framing follows the declared doors. The front is a cutaway sill.
  for (const c of room.localColliders.filter((w) => w.kind === 'wall')) {
    const x = (c.x0 + c.x1) / 2;
    const z = (c.z0 + c.z1) / 2;
    const w = c.x1 - c.x0;
    const depth = c.z1 - c.z0;
    const hor = w > depth;
    const front = z > d - 0.5;
    const height = front ? 0.38 : c.height || 2.8;
    box(timber, x, height / 2, z, w, height, depth);
    box(trim, x, 0.14, z, w + 0.05, 0.28, depth + 0.05);
    box(floor, x, height + 0.02, z, w + 0.08, 0.12, depth + 0.08);
    const length = Math.max(w, depth);
    const hull = hor && z < -d + 0.25 && !front;
    // The north wall is the ship's side (frames between portlights); east and west are transverse bulkheads with
    // evenly spaced stiffeners.
    let ticks: number[];
    if (hull) ticks = hullFrameStations(c.x0, c.x1).map((u) => u - x);
    else {
      const n = Math.max(1, Math.ceil(length / 1.6));
      ticks = Array.from({ length: n + 1 }, (_, k) => -length / 2 + (length * k) / n);
    }
    for (const q of ticks) box(trim, hor ? x + q : x, height / 2, hor ? z : z + q, hor ? 0.12 : w + 0.04, height + 0.08, hor ? depth + 0.04 : 0.12);
    if (!front) {
      box(floor, x, 0.86, z, w + 0.025, 0.09, depth + 0.025);
      box(trim, x, 2.12, z, w + 0.035, 0.13, depth + 0.035);
      // Planked lining between the frames.
      for (let k = 0; k < ticks.length - 1; k += 1) {
        const q = (at(ticks, k) + at(ticks, k + 1)) / 2;
        const span = at(ticks, k + 1) - at(ticks, k);
        box(boardLight, hor ? x + q : x, 1.49, hor ? z : z + q, hor ? span - 0.16 : w + 0.016, 1.13, hor ? depth + 0.016 : span - 0.16);
        for (const yy of [1.2, 1.49, 1.78]) box(timber, hor ? x + q : x, yy, hor ? z : z + q, hor ? span - 0.16 : w + 0.03, 0.014, hor ? depth + 0.03 : span - 0.16);
      }
      if (hull) {
        // Deck beams rest on the wall at every frame, each with a hanging knee; the deck above is cut away so only
        // the beam ends show.
        for (const q of ticks.slice(1, -1)) {
          box(timber, x + q, height + 0.24, z + 0.87, 0.2, 0.24, 1.7);
          beam(timber, [x + q, height - 0.62, z + 0.15], [x + q, height + 0.12, z + 0.62], 0.06);
        }
      }
    }
  }
  // Portlights, shelves and inset seed drawers stay on the rear (hull) wall.
  const rearWalls = room.localColliders.filter((c) => c.kind === 'wall' && c.z0 < -d + 0.25 && c.z1 - c.z0 < 0.5);
  const windowCenters = rearWalls.flatMap((c) => portlightStations(c.x0, c.x1).filter((x) => !room.localInteractions.some((i) => i.kind === 'drying-rack' && Math.abs(x - i.x) < 1.3)));
  for (const x of windowCenters) addPortlight(root, kit, glazing, x, 1.5, -d + 0.16);
  for (const x of [-h + 0.4, h - 0.4]) {
    box(floor, x, 0.57, -d + 0.35, 0.42, 1.1, 0.35);
    for (let k = 0; k < 4; k += 1) {
      box(timber, x, 0.18 + k * 0.24, -d + 0.55, 0.34, 0.19, 0.12);
      cyl(warm, x, 0.2 + k * 0.24, -d + 0.63, 0.026, 0.04, 0.026, 5).rotation.x = Math.PI / 2;
    }
  }
  // Suspended seed and medicine shelves occupy only intact wall segments.
  for (const c of rearWalls) {
    if (c.x1 - c.x0 < 1.4) continue;
    const sx = c.x0 + 0.5;
    const sz = -d + 0.24;
    box(floor, sx, 2.42, sz, 0.75, 0.11, 0.38);
    for (let k = 0; k < 3; k += 1) {
      cyl(k === 1 ? red : green, sx - 0.22 + k * 0.23, 2.59, sz, 0.078, 0.24, 0.06, 6);
      cyl(warm, sx - 0.22 + k * 0.23, 2.74, sz, 0.06, 0.055);
    }
    beam(trim, [sx - 0.28, 2.39, sz - 0.03], [sx - 0.28, 2.1, sz - 0.13], 0.026);
  }
  wallCut = false;
  /** One merged crop mesh per stage: three clumps with stalks, paired leaves per level and a crown. */
  const cropGeometry = (stage: CropStage): THREE.BufferGeometry => {
    const g = new THREE.Group();
    const clumps: readonly (readonly [number, number])[] = [
      [-0.4, -0.24],
      [0.32, -0.22],
      [-0.03, 0.28],
    ];
    const link = (a: Vec3, b: Vec3, r: number): void => {
      const start = new THREE.Vector3(...a);
      const end = new THREE.Vector3(...b);
      const v = end.clone().sub(start);
      const o = new THREE.Mesh(tag(new THREE.CylinderGeometry(r * 0.72, r, v.length(), 5)), green);
      o.position.copy(start.add(end).multiplyScalar(0.5));
      o.quaternion.setFromUnitVectors(new THREE.Vector3(0, 1, 0), v.normalize());
      g.add(o);
    };
    for (let k = 0; k < clumps.length; k += 1) {
      const [x, z] = at(clumps, k);
      const height = (stage === 'seeded' ? 0.12 : stage === 'growing' ? 0.42 : 0.76) * (1 - k * 0.07);
      const bendX = (k - 1) * 0.065;
      const bendZ = k % 2 === 0 ? 0.025 : -0.045;
      const tip: Vec3 = [x + bendX, 0.31 + height, z + bendZ];
      link([x, 0.305, z], tip, stage === 'seeded' ? 0.012 : 0.023);
      const levels = stage === 'seeded' ? 1 : stage === 'growing' ? 2 : 3;
      for (let level = 0; level < levels; level += 1) {
        const t = 0.35 + level * 0.22;
        const y = 0.31 + height * t;
        const stemX = x + bendX * t;
        const stemZ = z + bendZ * t;
        for (const side of [-1, 1]) {
          const angle = k * 1.45 + level * 0.9 + (side === 1 ? 0 : Math.PI);
          const size = stage === 'seeded' ? 0.095 : stage === 'growing' ? 0.17 : 0.2;
          const dx = Math.cos(angle) * size * 0.8;
          const dz = -Math.sin(angle) * size * 0.8;
          link([stemX, y, stemZ], [stemX + dx * 0.65, y + 0.045, stemZ + dz * 0.65], 0.01);
          const blade = leaf(g, stemX + dx, y + 0.045, stemZ + dz, size, angle, green);
          blade.rotation.z = 0.34 + ((k + level) % 3) * 0.17;
          blade.rotation.x = (side === 1 ? 1 : -1) * (0.16 + level * 0.09);
        }
      }
      // Small paired crown leaves visibly join the stalk tip rather than float.
      if (stage !== 'seeded') {
        for (const side of [-1, 1]) {
          const angle = k * 1.45 + side * 1.3;
          const size = stage === 'ready' ? 0.125 : 0.08;
          const o = leaf(g, tip[0] + Math.cos(angle) * size * 0.62, tip[1] - 0.03, tip[2] - Math.sin(angle) * size * 0.62, size, angle, green);
          o.rotation.z = 0.82;
        }
      }
    }
    g.updateMatrixWorld(true);
    const parts: THREE.BufferGeometry[] = [];
    g.traverse((o) => {
      if (isMesh(o)) {
        const source = o.geometry;
        let geom = source.clone();
        if (geom.index !== null) {
          const indexed = geom;
          geom = geom.toNonIndexed();
          indexed.dispose();
        }
        geom.applyMatrix4(o.matrixWorld);
        parts.push(geom);
        source.dispose();
      }
    });
    const result = mergeGeometries(parts, false);
    for (const p of parts) p.dispose();
    return result;
  };
  const cropGeometries: Readonly<Record<CropStage, THREE.BufferGeometry>> = { seeded: cropGeometry('seeded'), growing: cropGeometry('growing'), ready: cropGeometry('ready') };
  // The stage geometries are shared by the plots' meshes; the kit frees them with the scene.
  for (const g of Object.values(cropGeometries)) kit.own(g);
  for (const i of room.localInteractions) {
    const { x, z } = i.pos;
    if (i.kind === 'plot') {
      const w = i.w || 1.6;
      const dd = i.d || 1.4;
      box(trim, x, 0.115, z, w + 0.04, 0.23, dd + 0.04);
      box(soil, x, 0.235, z, w - 0.13, 0.14, dd - 0.13);
      for (const dz of [-dd / 2, dd / 2]) {
        box(timber, x, 0.29, z + dz, w + 0.12, 0.2, 0.11);
        box(floor, x, 0.395, z + dz, w + 0.14, 0.035, 0.13);
      }
      for (const dx of [-w / 2, w / 2]) box(timber, x + dx, 0.29, z, 0.1, 0.2, dd);
      for (let n = 0; n < 4; n += 1) box(timber, x - 0.55 + n * 0.36, 0.311, z, 0.03, 0.012, dd - 0.32);
      for (let n = 0; n < 13; n += 1) {
        const xx = x + (((n * 7) % 11) - 5) * 0.12;
        const zz = z + (((n * 3) % 7) - 3) * 0.15;
        mesh(new THREE.DodecahedronGeometry(0.025 + (n % 3) * 0.01, 0), n % 4 === 0 ? marker : soil, xx, 0.325, zz);
      }
      // One crop mesh per plot; its stage geometry follows the plot's state.
      const state = plotStates[i.index] ?? 'empty';
      const mat = kit.own(kit.finish(green.clone()));
      if (isCrop(state)) mat.color.set(cropColors[state]);
      // The geometry is shared (`cropGeometries`); a dynamic mesh is never batched or disposed by the world.
      const plant = new THREE.Mesh(isCrop(state) ? cropGeometries[state] : cropGeometries.seeded, mat);
      plant.position.set(x, 0, z);
      root.add(plant);
      plant.name = `crop:${i.id}`;
      tagMesh(plant, { dynamic: true });
      plant.visible = isCrop(state);
      dynamic.push({ id: i.id, kind: 'plot', mesh: plant, stateGeometry: cropGeometries });
      const sign = box(warm, x - w * 0.35, 0.52, z + dd * 0.5, 0.28, 0.18, 0.035);
      sign.rotation.z = -0.09;
      box(trim, x - w * 0.35, 0.43, z + dd * 0.5, 0.028, 0.24, 0.04);
      for (let t = 0; t < 3; t += 1) box(timber, x - w * 0.35, 0.48 + t * 0.045, z + dd * 0.5 + 0.021, 0.17 - t * 0.03, 0.012, 0.008);
    } else if (i.kind === 'potion-bench') {
      const w = i.w || 2;
      const dd = i.d || 1;
      for (const dx of [-w * 0.4, w * 0.4]) {
        for (const dz of [-dd * 0.35, dd * 0.35]) {
          box(timber, x + dx, 0.46, z + dz, 0.13, 0.92, 0.13);
          box(trim, x + dx, 0.12, z + dz, 0.17, 0.14, 0.17);
        }
      }
      box(trim, x, 0.16, z, w, 0.12, dd);
      box(floor, x, 0.98, z, w + 0.12, 0.17, dd + 0.12);
      box(timber, x, 0.82, z, w, 0.13, dd);
      for (let k = 0; k < 3; k += 1) {
        box(cream, x - 0.65 + k * 0.65, 0.83, z - 0.51, 0.54, 0.23, 0.05);
        ring(trim, x - 0.65 + k * 0.65, 0.84, z - 0.55, 0.055, 0.016, true);
      }
      // Three labelled bottles (tincture, tonic, salve base), not a filler row.
      const bottles = [green, red, blue];
      for (let n = 0; n < 3; n += 1) {
        const xx = x - 0.77 + n * 0.3;
        const zz = z - 0.25 + (n % 2) * 0.12;
        const ht = 0.34 - n * 0.06;
        const col = at(bottles, n);
        cyl(col, xx, 1.1 + ht / 2, zz, 0.095, ht, 0.08, 8);
        cyl(col, xx, 1.13 + ht, zz, 0.047, 0.12);
        cyl(warm, xx, 1.21 + ht, zz, 0.055, 0.05);
        box(cream, xx, 1.16 + ht / 2, zz - 0.088, 0.1, 0.1, 0.018);
      }
      cyl(trim, x + 0.67, 1.13, z + 0.28, 0.17, 0.16, 0.21);
      beam(floor, [x + 0.7, 1.15, z + 0.28], [x + 0.83, 1.45, z + 0.25], 0.055);
      box(cream, x - 0.2, 1.079, z + 0.29, 0.46, 0.023, 0.3);
      for (let k = 0; k < 4; k += 1) box(timber, x - 0.2, 1.094, z + 0.19 + k * 0.065, 0.3, 0.006, 0.01);
    } else if (i.kind === 'drying-rack') {
      const w = i.w || 2;
      for (const dx of [-w / 2, w / 2]) {
        box(timber, x + dx, 0.83, z, 0.08, 1.66, 0.09);
        box(trim, x + dx, 0.08, z, 0.28, 0.08, 0.4);
      }
      box(floor, x, 1.65, z, w + 0.18, 0.12, 0.12);
      box(trim, x, 0.53, z, w, 0.08, 0.08);
      for (let n = 0; n < 5; n += 1) {
        const xx = x - w * 0.4 + n * w * 0.2;
        beam(warm, [xx, 1.65, z], [xx, 1.22, z], 0.015);
        for (let k = 0; k < 5; k += 1) leaf(root, xx + ((k % 2) - 0.5) * 0.07, 1.1 - k * 0.09, z + ((k % 3) - 1) * 0.055, 0.18, n + k, green);
        ring(warm, xx, 1.28, z, 0.075, 0.016);
      }
    } else if (i.kind === 'watering-can') {
      cyl(blue, x, 0.32, z, 0.24, 0.52, 0.21, 10);
      ring(trim, x, 0.61, z, 0.2, 0.026);
      ring(floor, x - 0.2, 0.48, z, 0.22, 0.035, true);
      beam(blue, [x + 0.15, 0.3, z], [x + 0.43, 0.68, z], 0.062);
      const rose = cyl(warm, x + 0.43, 0.69, z, 0.1, 0.07, 0.1, 8);
      rose.rotation.z = -0.5;
    }
  }
  // Fresh-water tank on the forward bulkhead: filled by a pipe from the deck above, drawn off at the tap into the
  // watering can. It hangs on brackets above head height so it takes no floor space.
  {
    const tx = h - 0.36;
    const tz = 3.6;
    wallCut = true;
    box(timber, tx, 2.02, tz, 0.44, 0.9, 1.3);
    for (const yy of [1.66, 2.38]) box(trim, tx - 0.005, yy, tz, 0.46, 0.06, 1.32);
    for (const dz of [-0.5, 0.5]) beam(trim, [h - 0.12, 1.2, tz + dz], [tx - 0.18, 1.56, tz + dz], 0.035);
    beam(trim, [tx, 2.47, tz - 0.4], [tx, 2.92, tz - 0.4], 0.045);
    beam(trim, [tx - 0.22, 1.72, tz + 0.3], [tx - 0.4, 1.72, tz + 0.3], 0.03);
    cyl(trim, tx - 0.42, 1.65, tz + 0.3, 0.025, 0.14, 0.025, 5);
    wallCut = false;
  }
  for (const door of room.localDoors.filter((q) => q.side === 'south')) addCompanionLadder(root, kit, door.x, door.z - 1.3);
  // Level 2 is a physically different brass-bound overhead trellis, with no new obstacles.
  if (room.level > 1) {
    for (const x of [-h + 0.15, h - 0.15]) box(warm, x, 2.85, 0, 0.11, 0.15, room.depth);
    for (let z = -d + 0.6; z < d; z += 1.4) box(timber, 0, 2.87, z, room.width, 0.1, 0.065);
  }
  return { root, dynamic };
};
